import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@/components/ui';
import type { RoleMapTableVM } from './useRoleMapTable';
import styles from './RoleMapScopeDiffModal.module.css';

/**
 * EAA-1817: "Xem khác biệt với mặc định" (frame 59) — for one selected row,
 * while viewing the 'org' scope: does this (role_key, application) pair have
 * an override, and what's the +/- diff against the ecosystem default.
 *
 * WHOLESALE REPLACEMENT, not inheritance: `default`/`override` are two
 * independent key sets the server computes separately, and `added`/`removed`
 * is their diff — an override REPLACES the default's app_role list entirely
 * rather than adding to it. `RoleMapScopeDiffView` is `IsPortalAdmin`
 * (super-admin OR the acting org's own admin), so this is reachable by
 * org-admins too, unlike the impact/restore/re-point actions — matches
 * `vm.canManageOrg`, not `vm.canManageDefault`.
 */
export function RoleMapScopeDiffModal({ vm }: { vm: RoleMapTableVM }) {
    const { t } = useTranslation();
    const group = vm.scopeDiffTarget;
    const open = Boolean(group);
    const { data, isLoading, isError } = vm.scopeDiffQuery;

    if (!group) return null;

    const orgName = vm.actingOrgName ?? t('console.roleMap.scopeDiffModal.defaultOrgFallback');

    return (
        <Modal
            // EAA-1804: every kit component's root should be reachable by
            // className per the convention sweep — this Modal had none.
            className="role-map-scope-diff-modal"
            open={open}
            onCancel={vm.closeScopeDiff}
            title={t('console.roleMap.scopeDiffModal.title', {
                roleKey: group.role_key,
                appName: group.appName,
            })}
            footer={
                <>
                    <Button onClick={vm.closeScopeDiff}>{t('common.close')}</Button>
                    {data?.has_override && vm.canManageOrg && (
                        <Button danger onClick={vm.dropOverride} disabled={vm.dropOverrideBusy}>
                            {vm.dropOverrideBusy
                                ? t('console.roleMap.scopeDiffModal.dropOverrideBusy')
                                : t('console.roleMap.scopeDiffModal.dropOverride')}
                        </Button>
                    )}
                </>
            }
        >
            <div className={styles.scopeDiff}>
                {isLoading && (
                    <p className={styles.scopeDiffText}>
                        {t('console.roleMap.scopeDiffModal.loading')}
                    </p>
                )}
                {!isLoading && isError && (
                    <p className={styles.scopeDiffText}>
                        {t('console.roleMap.scopeDiffModal.errorText')}
                    </p>
                )}
                {!isLoading && data && !data.has_override && (
                    <p className={styles.scopeDiffText}>
                        {t('console.roleMap.scopeDiffModal.noOverrideText', { orgName })}
                    </p>
                )}
                {!isLoading && data && data.has_override && (
                    <>
                        <p className={styles.scopeDiffText}>
                            {t('console.roleMap.scopeDiffModal.hasOverrideText', { orgName })}
                        </p>
                        {data.added.length > 0 && (
                            <p className={styles.scopeDiffText}>
                                {t('console.roleMap.scopeDiffModal.addedLabel')}{' '}
                                {data.added.map((role, index) => (
                                    <span key={role}>
                                        {index > 0 && ', '}
                                        <code>+{role}</code>
                                    </span>
                                ))}
                            </p>
                        )}
                        {data.removed.length > 0 && (
                            <p className={styles.scopeDiffText}>
                                {t('console.roleMap.scopeDiffModal.removedLabel')}{' '}
                                {data.removed.map((role, index) => (
                                    <span key={role}>
                                        {index > 0 && ', '}
                                        <code>-{role}</code>
                                    </span>
                                ))}
                            </p>
                        )}
                        {data.added.length === 0 && data.removed.length === 0 && (
                            <p className={styles.scopeDiffText}>
                                {t('console.roleMap.scopeDiffModal.noDiffText')}
                            </p>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}
